// @flow

import type InputBase, { CommonInputProps } from './InputBase';
import type { ReadOnlyMap, AbstractInput, ValidationProblem } from './types';

class LazyMap<K, T, V> implements ReadOnlyMap<K, V> {
  _map: ReadOnlyMap<K, T>;
  _mapValue: T => V;

  constructor(map: ReadOnlyMap<K, T>, valueMapper: T => V) {
    this._map = map;
    this._mapValue = valueMapper;
  }

  get(k: K): ?V {
    const maybeValue = this._map.get(k);
    if (maybeValue !== null && maybeValue !== undefined) {
      return this._mapValue(maybeValue);
    }
    return maybeValue;
  }

  has(k: K) {
    return this._map.has(k);
  }

  *entries(): Iterator<[K, V]> {
    for (const [k, t] of this._map.entries()) {
      yield [k, this._mapValue(t)];
    }
  }
  forEach(callback: (V, K, LazyMap<K, T, V>) => void) {
    this._map.forEach((t, k) => callback(this._mapValue(t), k, this));
  }

  keys(): Iterator<K> {
    return this._map.keys();
  }

  *values(): Iterator<V> {
    for (const t of this._map.values()) {
      yield this._mapValue(t);
    }
  }
  // $FlowFixMe
  [Symbol.iterator]() {
    return this.entries();
  }
}

/**
 * This is a proxy to the underlying input that exposes only the methods
 * actually intended to be used in the various input event handlers.
 */
export default class InputApi<ValueType> implements AbstractInput<ValueType> {
  _input: InputBase<ValueType>;

  // This exists because there is literally NO OTHER (documented) WAY to prevent jest from
  // attempting to pretty-print the entire react object, including fiber renderer, dom, and a
  // whole lot of circular references, which inevitably crashes the node process
  // If ANY object we assert against contains ANY references to ANY react rendered react element,
  // in ANY way, this will happen, so instead we just keep indirect references via WeakMap.
  // $FlowFixMe - This type was breaking with Babel 7. Type was WeakMap<any, InputBase<ValueType, CommonInputProps<ValueType>>,>
  static aStupidFuckingMap = new WeakMap();

  constructor(input: InputBase<ValueType, any, any>) {
    InputApi.aStupidFuckingMap.set(this, input);
    this._input = input;
  }

  get _input(): InputBase<ValueType, CommonInputProps<ValueType>> {
    const input = InputApi.aStupidFuckingMap.get(this);
    if (input === null || input === undefined) {
      throw new Error('This should not be possible.');
    }
    // $FlowFixMe - type was breaking above so this is erroring as well.
    return input;
  }

  // eslint-disable-next-line getter-return
  get currentValue(): ValueType {
    return this._input.currentValue;
  }

  // eslint-disable-next-line getter-return
  get committedValue(): ValueType {
    return this._input.committedValue;
  }

  get receivedValue(): ValueType {
    return this._input.receivedValue;
  }

  get sourceValue(): ValueType {
    return this._input.sourceValue;
  }

  // eslint-disable-next-line consistent-return,getter-return
  get parent(): ?InputApi<any> {
    // eslint-disable-next-line prefer-destructuring
    const parent = this._input.parent;
    if (parent) {
      return parent.api;
    }
  }

  // eslint-disable-next-line getter-return, consistent-return
  get inputs(): ?LazyMap<any, InputBase<any, CommonInputProps<any>>, InputApi<any>> {
    if (this._input.inputs) {
      return new LazyMap(this._input.inputs, input => input.api);
    }
  }

  get name(): ?any {
    return this._input.name;
  }

  get validationProblems(): Array<ValidationProblem<any>> {
    return this._input.validationProblems;
  }

  set currentValue(value: ValueType) {
    this._input.currentValue = value;
  }

  set committedValue(value: ValueType) {
    this._input.committedValue = value;
  }

  validate(): boolean {
    const input = this._input;
    return input.validate();
  }

  flush() {
    this._input.flush();
  }
  isFlushed(): boolean {
    return this._input.isFlushed();
  }
  hasUnflushedChanges(): boolean {
    return this._input.hasUnflushedChanges();
  }
  reset() {
    this._input.reset();
  }
  isValid(): boolean {
    return this._input.isValid();
  }
  hasValue(): boolean {
    return this._input.hasValue();
  }
  addValidationProblem(...problems: Array<ValidationProblem<ValueType>>) {
    this._input.addValidationProblem(...problems);
  }

  focus() {
    // $FlowFixMe - Short-term solution. Only atomic inputs should be focusable!
    this._input.focus();
  }
}
